import {
  FORGE_ELEMENT_INFO,
  FORGE_ELEMENTS,
  INFUSE_MAX,
  isForgeElement,
  MONSTERS,
  QUESTS,
  SCHOOLS,
  STATUSES,
  type GmCommand,
  type GmFlag,
  type GmTeleport,
  type PlayerCommand,
  type World,
} from '@nine/shared';
import { closeButton, h } from './dom';

const FLAGS: [GmFlag, string][] = [
  ['god', 'อมตะ'],
  ['oneHit', 'ตีทีเดียวตาย'],
  ['noCooldown', 'ไม่มีคูลดาวน์'],
  ['infQi', 'ลมปราณ/ความโกรธไม่จำกัด'],
];
const TELEPORTS: [GmTeleport, string][] = [
  ['spawn', 'จุดเกิด'],
  ['plaza', 'ลานศิลา'],
  ['wolves', 'ป่าหมาป่า'],
  ['bandits', 'ค่ายโจร'],
  ['lair', 'ลานเลือด (บอส)'],
  ['arena', 'ลานจอมมาร (บอสโลก)'],
];
const SPEEDS = [0.25, 0.5, 1, 2, 4];

export interface GmHooks {
  save(): void;
  wipeSave(): void;
}

/**
 * Development menu (` key): cheats and test tools. Everything that touches the game goes through
 * GM world commands; only game speed and the FPS meter are client-side.
 */
export class GmPanel {
  /** Simulation speed multiplier applied by the main loop. */
  timeScale = 1;
  private readonly root: HTMLElement;
  private readonly flagButtons = new Map<GmFlag, HTMLButtonElement>();
  private readonly speedButtons: HTMLButtonElement[] = [];
  private readonly fps = h('div', { id: 'gmFps', hidden: true });
  private fpsT = 0;
  private frames = 0;

  constructor(
    private readonly world: World,
    private readonly send: (c: PlayerCommand) => void,
    hooks: GmHooks,
  ) {
    const gm = (cmd: GmCommand) => this.send({ t: 'gm', cmd });
    const btn = (label: string, fn: () => void, cls = 'btn small ghost') => {
      const b = h('button', { type: 'button', class: cls }, label);
      b.addEventListener('click', fn);
      return b;
    };
    const select = (opts: [string, string][]) =>
      h('select', {}, ...opts.map(([v, t]) => h('option', { value: v }, t)));
    const section = (title: string, ...rows: (Node | string)[]) =>
      h('section', {}, h('h4', {}, title), h('div', { class: 'gm-row' }, ...rows));

    const monster = select(Object.values(MONSTERS).map((m) => [m.id, m.name]));
    const count = select(['1', '3', '5', '10'].map((n) => [n, `×${n}`]));
    const quest = select(QUESTS.map((q, i) => [String(i), `${i + 1}. ${q.title}`]));
    const school = select([
      ['', 'ไม่มีสำนัก'],
      ...Object.values(SCHOOLS).map((s): [string, string] => [s.id, s.name]),
    ]);
    const plus = select(['0', '7', '10', '15'].map((n) => [n, `+${n}`]));
    const element = select([
      ['', 'ไม่มีธาตุ'],
      ...FORGE_ELEMENTS.map((el): [string, string] => [el, `ธาตุ${FORGE_ELEMENT_INFO[el].name} 8`]),
    ]);
    const status = select(Object.values(STATUSES).map((s) => [s.id, `${s.glyph} ${s.name}`]));
    const stacks = select(['1', '2', '3'].map((n) => [n, `${n} ชั้น`]));
    const onWho = select([
      ['target', 'เป้าหมาย'],
      ['self', 'ตัวเอง'],
    ]);

    for (const [f, label] of FLAGS) {
      const b = btn(label, () => gm({ op: 'toggle', flag: f }), 'btn small ghost gm-flag');
      this.flagButtons.set(f, b);
    }
    for (const sp of SPEEDS) {
      const b = btn(`×${sp}`, () => (this.timeScale = sp), 'btn small ghost gm-flag');
      b.dataset.speed = String(sp);
      this.speedButtons.push(b);
    }
    const fpsBtn = btn('FPS', () => (this.fps.hidden = !this.fps.hidden));

    this.root = h(
      'aside',
      { id: 'gm', hidden: true },
      h(
        'header',
        {},
        h('b', {}, 'GM · โหมดพัฒนา'),
        h('kbd', {}, '`'),
        closeButton(() => this.toggle(), 'ปิด (`)'),
      ),
      section(
        'ทรัพยากร',
        btn('+1,000 ฝึกปรือ', () => gm({ op: 'cultivation', amount: 1000 })),
        btn('+10,000 ฝึกปรือ', () => gm({ op: 'cultivation', amount: 10000 })),
        btn('+1,000 ตำลึง', () => gm({ op: 'gold', amount: 1000 })),
        btn('+1,000 หยกเซียน', () => gm({ op: 'jade', amount: 1000 })),
        btn('+500 แต้มตั๋วยุทธภพ', () => gm({ op: 'passXp', amount: 500 })),
        btn('ฝึกเสร็จทันที', () => gm({ op: 'finishTraining' })),
        btn('+200 คะแนนบันได', () => gm({ op: 'ladderRating', amount: 200 })),
        btn('ประลองยุทธ: ขึ้นวันใหม่', () => gm({ op: 'arenaNextDay' })),
        btn('เวลา +3 ชั่วโมง', () => gm({ op: 'hours', amount: 3 })),
        btn('ฟื้นฟูเต็ม', () => gm({ op: 'restore' })),
        btn('ความโกรธเต็ม', () => gm({ op: 'rage' })),
      ),
      section('โกง', ...this.flagButtons.values()),
      section(
        'ความก้าวหน้า',
        btn('เรียนทุกวิชา', () => gm({ op: 'learnAllArts' })),
        btn('ท่าทุกท่าขั้นสูงสุด', () => gm({ op: 'maxSkills' })),
        btn('กำลังภายในขั้นสูงสุด', () => gm({ op: 'maxNeigong' })),
        btn('ไอเทมทุกชิ้น', () => gm({ op: 'giveAllItems' })),
        btn('ทุกฉายา สกิน พาหนะ', () => gm({ op: 'allTitles' })),
        btn('ฉายาเถาฉางเยี่ย', () => gm({ op: 'title', id: 'dugu' })),
        school,
        btn('ตั้งสำนัก', () => gm({ op: 'school', id: school.value || null })),
      ),
      section(
        'ตีบวกอาวุธที่ถือ',
        plus,
        element,
        btn('ตั้งค่า', () =>
          gm({
            op: 'forge',
            plus: Number(plus.value),
            el: isForgeElement(element.value) ? element.value : null,
            lv: INFUSE_MAX,
          }),
        ),
      ),
      section(
        'ภารกิจ',
        quest,
        btn('ไปที่ภารกิจนี้', () => gm({ op: 'questSet', index: Number(quest.value) })),
        btn('ทำภารกิจปัจจุบันสำเร็จ', () => gm({ op: 'questComplete' })),
      ),
      section(
        'โลก',
        monster,
        count,
        btn('เรียกมอนสเตอร์', () => gm({ op: 'spawn', monster: monster.value, count: Number(count.value) })),
        btn('เรียกบอส', () => gm({ op: 'spawnBoss' })),
        // Guild-war load test: fighters of every school and rank on screen at once.
        btn('ฝูงชน ×50', () => gm({ op: 'crowd', count: 50 })),
        btn('ฝูงชน ×150', () => gm({ op: 'crowd', count: 150 })),
        btn('ฝูงชน ×200 · ทดสอบวอร์กิล', () => gm({ op: 'crowd', count: 200 })),
        btn('ปราบศัตรูรอบตัว', () => gm({ op: 'killNearby' })),
        btn('ดาบไร้เงา เปิด/ปิดแดง', () => gm({ op: 'rivalPk' })),
        ...TELEPORTS.map(([to, label]) => btn(`วาร์ป: ${label}`, () => gm({ op: 'teleport', to }))),
      ),
      section(
        'สถานะ',
        status,
        stacks,
        onWho,
        btn('ใส่สถานะ', () =>
          gm({
            op: 'status',
            id: status.value,
            stacks: Number(stacks.value),
            on: onWho.value as 'self' | 'target',
          }),
        ),
        btn('ล้างสถานะตัวเอง', () => gm({ op: 'clearStatuses' })),
      ),
      section(
        'ระบบ',
        h('span', { class: 'gm-label' }, 'ความเร็วเกม'),
        ...this.speedButtons,
        fpsBtn,
        btn('บันทึกตอนนี้', () => hooks.save()),
        btn('ลบเซฟแล้วเริ่มใหม่', () => {
          if (confirm('ลบเซฟทั้งหมดแล้วเริ่มใหม่?')) hooks.wipeSave();
        }),
      ),
    );
    // Clicks on the menu must not walk the character.
    this.root.addEventListener('pointerdown', (e) => e.stopPropagation());
    document.body.append(this.root, this.fps);
  }

  get isOpen(): boolean {
    return !this.root.hidden;
  }

  toggle(): void {
    this.root.hidden = !this.root.hidden;
  }

  /** Reflects toggles and speed, and updates the FPS meter. */
  sync(dt: number): void {
    this.frames++;
    this.fpsT += dt;
    if (this.fpsT >= 0.5) {
      this.fps.textContent = `${Math.round(this.frames / this.fpsT)} FPS · ×${this.timeScale}`;
      this.frames = 0;
      this.fpsT = 0;
    }
    if (this.root.hidden) return;
    for (const [f, b] of this.flagButtons) b.classList.toggle('on', this.world.gmFlags[f]);
    for (const b of this.speedButtons) b.classList.toggle('on', Number(b.dataset.speed) === this.timeScale);
  }
}
